import React, { useEffect, useState } from 'react';
import '../../assets/css/Demographics/Demographics.css';
import { Button, Col, Row, ProgressBar, Form } from 'react-bootstrap';
import bannerImg from '../../assets/images/homepage-card.png';
import ReviewQuestions from './ReviewQuestions';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import { calculateAnswered, formatPostalCode, validateCanadianPostalCode } from '../../helpers/utils';
import ReactSelect from 'react-select';

const QuestionsAnswers = () => {
    const { getToken } = useAuth();
    const [user, setUser] = useLocalStorage('user', null);
    const token = getToken();
    const [step, setStep] = useState(0);
    const [responses, setResponses] = useState({});
    const [skipped, setSkipped] = useState([]);
    const [reviewMode, setReviewMode] = useState(false);
    const [incomeCategory, setIncomeCategory] = useState(null);

    const demographics = user?.demographics;

    const decryptedDemographics = async () => {
        if (demographics) {
            const parsed = JSON.parse(demographics);

            // Convert legacy single-answer to array format for multi-select
            if (parsed['Employment Status']?.[10] && !Array.isArray(parsed['Employment Status'][10])) {
                parsed['Employment Status'][10] = [parsed['Employment Status'][10]];
            }

            setResponses(parsed);

            // Check if income question was answered and set incomeCategory accordingly
            const incomeResponse = parsed['Income']?.[11];
            if (incomeResponse && incomeResponse !== 'Prefer not to answer') {
                setIncomeCategory(incomeResponse);
            }
        }
    };

    useEffect(() => {
        decryptedDemographics();
    }, [demographics, user]);

    // Base questions that are always present
    const baseQuestions = [
        {
            id: 2,
            screenTitle: 'Home Address',
            weighting: 10,
            question: 'What is your Address with postal code?',
            type: 'multi',
            fields: [
                { name: 'address', placeholder: 'Enter address', type: 'text' },
                { name: 'apartment', placeholder: 'Enter Apartment', type: 'number' },
                { name: 'city', placeholder: 'Enter City', type: 'text' },
                { name: 'state', placeholder: 'Enter province or state', type: 'dropdown', options: ['Ontario', 'Quebec', 'British Columbia', 'Alberta', 'Manitoba'] },
                { name: 'postalCode', placeholder: 'Enter postal code', type: 'text' },
                { name: 'country', placeholder: 'Enter Country', type: 'text' }
            ]
        },
        {
            id: 3,
            screenTitle: 'Age',
            weighting: 9,
            question: 'What is your age group?',
            type: 'dropdown',
            options: ['18 to 29', '30 to 39', '40 to 49', '50 to 59', '60 to 69', '70 to 79', '80 or over']
        },
        {
            id: 4,
            screenTitle: 'Gender',
            weighting: 9,
            question: 'What is your gender?',
            type: 'dropdown',
            options: ['Female', 'Male', 'Other', 'Prefer not to answer']
        },
        {
            id: 5,
            screenTitle: 'Home transit agency',
            weighting: 9,
            question: 'Which transit agency do you use?',
            type: 'dropdown',
            options: ['Hamilton Street Railway', 'London Transit', 'Brampton Transit']
        },
        {
            id: 6,
            screenTitle: 'Rider type',
            weighting: 9,
            question: 'How do you use transit?',
            type: 'dropdown',
            options: ['Occasional transit rider', 'Commuter (strictly use transit to/from work)', 'All purpose (for commuting, errands, leisure, etc.)', 'Other']
        },
        {
            id: 7,
            screenTitle: 'Home ownership',
            weighting: 9,
            question: 'How would you describe your dwelling situation?',
            type: 'dropdown',
            options: ['Home owner', 'Home renter', 'Live rent-free', 'Homeless', 'Other']
        },
        {
            id: 8,
            screenTitle: 'Car ownership?',
            weighting: 9,
            question: 'Do you have access to a car for personal transportation?',
            type: 'dropdown',
            options: ['Yes, any time', 'Yes, sometimes', 'No']
        },
        {
            id: 9,
            screenTitle: 'Educational level (highest level of education)',
            weighting: 9,
            question: 'What is your highest educational qualification?',
            type: 'dropdown',
            options: [
                'Less than high school diploma',
                'High school diploma',
                'Trade certificate or diploma',
                'Non-university certificate or diploma',
                'University certificate, diploma or degree',
                `University degree above the bachelor's level`
            ]
        },
        {
            id: 10,
            screenTitle: 'Employment Status',
            weighting: 9,
            question: 'Which of these describe you?',
            type: 'multi-select',
            options: [
                'Full-time employed',
                'Part-time employed',
                'Not employed for pay',
                'Caregiver (e.g., children, elderly)',
                'Homemaker',
                'Full-time student',
                'Part-time student',
                'Other'
            ]
        },
        {
            id: 11,
            screenTitle: 'Income',
            weighting: 9,
            question: 'Which of these describes your income last year?',
            type: 'dropdown',
            options: ['Less than $30,000', '$30,000 and more', 'Prefer not to answer']
        },
        {
            id: 13,
            screenTitle: 'Banking',
            weighting: 9,
            question: 'Do you have a bank account?',
            type: 'dropdown',
            options: ['Have at least one bank account', 'Have no bank account', 'Other']
        }
    ];

    // Dynamic income details question
    const incomeDetailsQuestion = {
        id: 12,
        screenTitle: 'Income Details',
        weighting: 9,
        question: 'Please select your specific income range:',
        type: 'dropdown',
        options:
            incomeCategory === 'Less than $30,000' && incomeCategory !== 'Prefer not to answer'
                ? [
                      'Less than $5,000',
                      '$5,000 to less than $10,000',
                      '$10,000 to less than $15,000',
                      '$15,000 to less than $20,000',
                      '$20,000 to less than $25,000',
                      '$25,000 to less than $30,000',
                      'Prefer not to answer'
                  ]
                : [
                      '$30,000 to less than $40,000',
                      '$40,000 to less than $50,000',
                      '$50,000 to less than $60,000',
                      '$60,000 to less than $70,000',
                      '$70,000 to less than $80,000',
                      '$80,000 to less than $90,000',
                      '$90,000 to less than $100,000',
                      '$100,000 and over',
                      'Prefer not to answer'
                  ]
    };

    // Dynamically determine the questions array based on incomeCategory
    const questions = incomeCategory ? [...baseQuestions.slice(0, 11), incomeDetailsQuestion, ...baseQuestions.slice(11)] : baseQuestions;

    const totalQuestions = questions.length;
    const answeredCount = calculateAnswered(responses);
    const progress = Math.round((answeredCount / totalQuestions) * 100);

    const handleEditAnswer = (fieldName, value) => {
        if (fieldName === 'postalCode') {
            // Limit postal code input to 7 characters
            let formattedValue = formatPostalCode(value).slice(0, 7);

            // Update responses with formatted value
            setResponses((prev) => ({
                ...prev,
                [questions[step - 1].screenTitle]: {
                    ...prev[questions[step - 1].screenTitle],
                    [fieldName]: formattedValue,
                    weighting: questions[step - 1].weighting
                }
            }));

            // Validate only when the full postal code is entered
            if (formattedValue.length === 7 && !validateCanadianPostalCode(formattedValue)) {
                toast.error('Invalid Canadian postal code format (e.g., A1A 1A1)');
            }

            return;
        }

        // Handle income category selection
        if (questions[step - 1].id === 11) {
            // Reset incomeCategory if "Prefer not to answer" is selected
            if (value === 'Prefer not to answer') {
                setIncomeCategory(null);
            } else {
                setIncomeCategory(value);
            }
        }

        // For other fields, update responses normally
        setResponses((prev) => ({
            ...prev,
            [questions[step - 1].screenTitle]: {
                ...prev[questions[step - 1].screenTitle],
                [fieldName]: value,
                weighting: questions[step - 1].weighting
            }
        }));
    };

    const handleNext = () => {
        const currentQuestion = questions[step - 1];
        const selectedAnswer = responses[currentQuestion.screenTitle];

        // If the user selects "Prefer not to answer", skip automatically
        if (selectedAnswer && Object.values(selectedAnswer).includes('Prefer not to answer')) {
            handleSkip();
            return;
        }

        if (currentQuestion.screenTitle === 'Home Address') {
            const postalCode = responses[currentQuestion.screenTitle]?.postalCode || '';

            if (postalCode.length < 7) {
                toast.error('Please enter a complete postal code (e.g., A1A 1A1).');
                return;
            }

            if (!validateCanadianPostalCode(postalCode)) {
                toast.error('Invalid Canadian postal code format (e.g., A1A 1A1)');
                return;
            }
        }

        setStep((prev) => prev + 1);
    };

    const handleBack = () => setStep((prev) => Math.max(prev - 1, 0));

    const handleSkip = () => {
        setSkipped([...skipped, step]);
        setStep((prev) => prev + 1);
    };

    // Helper to determine the expected total questions based on responses
    const getExpectedTotalQuestions = () => {
        // Check if income response exists and would trigger showing the details question
        const incomeResponse = responses['Income']?.[11];
        return incomeResponse && incomeResponse !== 'Prefer not to answer' ? 13 : 12;
    };

    return (
        <div style={{ position: 'relative', top: -45 }}>
            {step > 0 && !reviewMode && (
                <Row className="d-flex justify-content-center">
                    <Col xs={12} md={8} className="">
                        <div className="d-flex justify-content-end">
                            <Button
                                onClick={() => {
                                    setReviewMode(true);
                                }}
                                className="theme-btn"
                            >
                                List View
                            </Button>
                        </div>
                    </Col>
                </Row>
            )}

            {!reviewMode && (
                <Row className="d-flex justify-content-center mt-5">
                    <Col xs={12} md={8} className="shadow-lg p-5 m-3">
                        {step === 0 ? (
                            <>
                                <div
                                    className="text-white d-flex justify-content-center align-items-center"
                                    style={{
                                        backgroundImage: `url(${bannerImg})`,
                                        height: '400px',
                                        borderRadius: '10px',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat'
                                    }}
                                >
                                    <div className="text-center">
                                        <h4>The more questions you answer, the higher your rewards.</h4>
                                        <small>
                                            <strong>Note</strong>: User's data is stored safely and protected and compliant with data security standards.
                                        </small>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <Button className="mt-3 theme-btn" onClick={() => setStep(1)}>
                                        {demographics ? (answeredCount === getExpectedTotalQuestions() ? 'Update' : 'Continue') : 'Start'}
                                    </Button>
                                </div>
                            </>
                        ) : step <= totalQuestions ? (
                            <>
                                {/* screen title */}
                                <h3 className="text-center">{questions[step - 1].screenTitle}</h3>
                                <h5>
                                    Question {step} of {totalQuestions} (Answered {answeredCount})
                                </h5>
                                <ProgressBar variant="success" now={progress} label={`${progress}%`} className="mb-3" />
                                <h4>{questions[step - 1].question}</h4>
                                <div className="">
                                    <Form className="mt-3">
                                        {(() => {
                                            const q = questions[step - 1];
                                            if (q.type === 'multi') {
                                                return q.fields.map((field) => {
                                                    if (field.type === 'dropdown') {
                                                        return (
                                                            <select
                                                                className="mb-2 question-answer-selector shadow-none w-100"
                                                                key={field.name}
                                                                value={responses[q.screenTitle]?.[field.name] || ''}
                                                                onChange={(e) => handleEditAnswer(field.name, e.target.value)}
                                                            >
                                                                <option value="">Select an option</option>
                                                                {field.options.map((option, index) => (
                                                                    <option key={index} value={option}>
                                                                        {option}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        );
                                                    }
                                                    return (
                                                        <Form.Control
                                                            key={field.name}
                                                            type={field.type}
                                                            placeholder={field.placeholder}
                                                            value={responses[q.screenTitle]?.[field.name] || ''}
                                                            onChange={(e) => handleEditAnswer(field.name, e.target.value)}
                                                            className="mb-2 question-answer-input form-control shadow-none"
                                                        />
                                                    );
                                                });
                                            }
                                            if (q.type === 'multi-select') {
                                                return (
                                                    <ReactSelect
                                                        isMulti
                                                        options={q.options.map((option) => ({ label: option, value: option }))}
                                                        value={(responses[q.screenTitle]?.[q.id] || []).map((value) => ({
                                                            label: value,
                                                            value
                                                        }))}
                                                        onChange={(selectedOptions) => {
                                                            const selectedValues = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                                                            handleEditAnswer(q.id, selectedValues);
                                                        }}
                                                        className="mb-2 question-answer-selector shadow-none w-100"
                                                    />
                                                );
                                            }
                                            if (q.type === 'dropdown') {
                                                return (
                                                    <Form.Select
                                                        className="question-answer-selector shadow-none mb-2"
                                                        onChange={(e) => handleEditAnswer(q.id, e.target.value)}
                                                        value={responses[q.screenTitle]?.[q.id] || ''}
                                                    >
                                                        <option value="">Select an option</option>
                                                        {q.options.map((option, index) => (
                                                            <option key={index} value={option}>
                                                                {option}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                );
                                            }
                                            return (
                                                <Form.Control
                                                    className="mb-2 question-answer-input form-control shadow-none"
                                                    type={q.type}
                                                    placeholder={q.placeholder}
                                                    value={responses[q.screenTitle]?.[q.id] || ''}
                                                    onChange={(e) => handleEditAnswer(q.id, e.target.value)}
                                                />
                                            );
                                        })()}
                                    </Form>
                                </div>
                                <div className="mt-3 d-flex gap-2 justify-content-between">
                                    <div className="d-flex gap-2">
                                        {step > 1 && (
                                            <Button className="theme-outline-cancel-btn" onClick={handleBack}>
                                                Back
                                            </Button>
                                        )}
                                        <Button className="theme-cancel-btn" onClick={handleSkip}>
                                            Skip
                                        </Button>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button className="theme-btn" onClick={handleNext}>
                                            Next
                                        </Button>
                                        <Button
                                            className="theme-outline-cancel-btn"
                                            onClick={() => {
                                                // saveDemographics
                                                const webApi = new WebApiCalls();
                                                const token = getToken();

                                                if (token) {
                                                    webApi
                                                        .saveDemographics(token, {
                                                            demographics: JSON.stringify({ ...responses, progress })
                                                        })
                                                        .then((res) => {
                                                            if (res?.code === 200) {
                                                                toast.success(res?.desc);
                                                                setStep(0);
                                                                setUser({
                                                                    ...user,
                                                                    demographics: JSON.stringify(responses)
                                                                });
                                                                setReviewMode(false);
                                                            }
                                                        })
                                                        .catch((err) => {
                                                            // handle error
                                                        });
                                                }
                                            }}
                                        >
                                            Save & Exit
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3>You've completed the questionnaire!</h3>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        setReviewMode(true);
                                    }}
                                >
                                    Review Answers
                                </Button>
                            </>
                        )}
                    </Col>
                </Row>
            )}

            {reviewMode && <ReviewQuestions qData={responses} progress={progress} setReviewMode={setReviewMode} setStep={setStep} />}
        </div>
    );
};

export default QuestionsAnswers;
